import React, { useState, useEffect, useMemo } from 'react';
import { AppBar, Toolbar, IconButton, Menu, MenuItem, Typography, Box, Avatar, useTheme, useMediaQuery, Tooltip, ListItemText, ButtonBase } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Menu as MenuIcon, Search as SearchIcon } from '@mui/icons-material';
import { canManageSistemaUsers, effectiveUserRoles } from '../utils/sistemaAuthz';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getSiteName } from '../config/appConfig';
import { resolveTenantAccent } from '../utils/tenantAccentColor';
import ChangePasswordDialog from './ChangePasswordDialog';
import BuscadorAccesosModal from './BuscadorAccesosModal';
import PageHelpButton from './help/PageHelpButton';
import FechaOperativaChip from './FechaOperativaChip';
import TenantSwitcher from './TenantSwitcher';
import { useFechaOperativa } from '../contexts/FechaOperativaContext';

interface HeaderProps {
  onMenuClick?: () => void;
  desktopOpen?: boolean;
  onDesktopToggle?: () => void;
  /** Drawer de ayuda abierto (Layout reserva padding al contenido). */
  pageHelpOpen?: boolean;
  onPageHelpOpenChange?: (open: boolean) => void;
}

const Header: React.FC<HeaderProps> = ({
  onMenuClick,
  desktopOpen = true,
  onDesktopToggle,
  pageHelpOpen,
  onPageHelpOpenChange,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, tenantId, tenantName, tenantCode, tenants, switchTenant } = useAuth();
  const { requestOpenEditor } = useFechaOperativa();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [buscadorOpen, setBuscadorOpen] = useState(false);
  const [apkDownloading, setApkDownloading] = useState(false);
  const apkDownloadUrl = 'https://oneapp.neos.com.ar/assets/eone-app.apk';
  const apkFileName = 'eone-app.apk';
  const puedeAdministrar = canManageSistemaUsers(effectiveUserRoles(user?.roles));
  const puedeCambiarTenant = tenants.length > 1;
  const tenantAccent = useMemo(
    () => resolveTenantAccent(tenantId, tenants),
    [tenantId, tenants]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        setBuscadorOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    const siteName = getSiteName();
    if (puedeCambiarTenant && tenantName) {
      const pathLabel = location.pathname === '/' || location.pathname === '/menu-principal'
        ? siteName
        : location.pathname.split('/').filter(Boolean)[0] || siteName;
      document.title = `${tenantName} · ${pathLabel}`;
    } else {
      document.title = siteName;
    }
  }, [puedeCambiarTenant, tenantName, location.pathname]);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    logout();
  };

  const handleLogoutAll = () => {
    handleClose();
    logout({ allTabs: true });
  };

  const handleChangePassword = () => {
    handleClose();
    setChangePasswordOpen(true);
  };

  const handleEditMyProfile = () => {
    handleClose();
    if (user?.username) {
      navigate(`/sistema/usuarios/${encodeURIComponent(user.username)}`);
    }
  };

  const handleDownloadApk = async () => {
    handleClose();
    if (apkDownloading) {
      return;
    }

    setApkDownloading(true);
    try {
      const response = await fetch(apkDownloadUrl, { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const contentType = response.headers.get('content-type') ?? '';
      if (contentType.includes('text/html')) {
        throw new Error('El servidor devolvió HTML en lugar del APK');
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = apkFileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      window.open(apkDownloadUrl, '_blank', 'noopener,noreferrer');
    } finally {
      setApkDownloading(false);
    }
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor:
          puedeCambiarTenant && tenantAccent
            ? `color-mix(in srgb, ${tenantAccent} 15%, #ffffff)`
            : '#ffffff',
        color: '#000000',
        boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
        borderLeft: puedeCambiarTenant && tenantAccent ? `6px solid ${tenantAccent}` : undefined,
        borderBottom:
          puedeCambiarTenant && tenantAccent ? `1px solid ${alpha(tenantAccent, 0.35)}` : undefined,
      }}
    >
      <Toolbar
        sx={{
          minHeight: { xs: 56, sm: 64 },
          px: { xs: 1, sm: 2 },
          gap: { xs: 1, md: 1.5 },
        }}
      >
        <Box
          sx={{
            flex: { xs: '0 0 auto', md: '1 1 0' },
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
          }}
        >
          {isMobile && (
            <Tooltip title="Abrir menú">
              <IconButton
                color="inherit"
                aria-label="abrir menú"
                edge="start"
                size="small"
                onClick={onMenuClick}
                sx={{
                  mr: 0.5,
                  flexShrink: 0,
                  color: '#00548f',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
            </Tooltip>
          )}
          <Box
            component={Link}
            to="/menu-principal"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              textDecoration: 'none',
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src="/assets/eone-logo.png"
              alt="eOne"
              sx={{
                height: { xs: 32, sm: 40, md: 64 },
                width: 'auto',
                objectFit: 'contain',
              }}
            />
          </Box>
        </Box>

        {isMobile && tenantName && (
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              justifyContent: 'center',
              px: 1,
            }}
          >
            <TenantSwitcher
              variant="compact"
              tenants={tenants}
              tenantId={tenantId}
              tenantName={tenantName}
              tenantCode={tenantCode}
              onSwitch={async (id) => {
                await switchTenant(id);
              }}
            />
          </Box>
        )}

        {!isMobile && (
          <Box
            sx={{
              flex: '0 1 320px',
              minWidth: 160,
              maxWidth: 360,
              display: 'flex',
              justifyContent: 'center',
              px: 0.5,
            }}
          >
            <Tooltip title="Buscar pantallas y funcionalidades (Ctrl+K)">
              <ButtonBase
                onClick={() => setBuscadorOpen(true)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  height: 36,
                  width: '100%',
                  px: 1.5,
                  borderRadius: 999,
                  border: '1px solid',
                  borderColor: alpha('#00548f', 0.18),
                  bgcolor: alpha('#00548f', 0.04),
                  color: '#00548f',
                  justifyContent: 'flex-start',
                  transition: 'background-color 0.15s ease, border-color 0.15s ease',
                  '&:hover': {
                    bgcolor: alpha('#00548f', 0.08),
                    borderColor: alpha('#00548f', 0.35),
                  },
                }}
              >
                <SearchIcon sx={{ fontSize: 18, color: theme.palette.primary.main, flexShrink: 0 }} />
                <Typography
                  noWrap
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    textAlign: 'left',
                    fontSize: '0.875rem',
                    color: 'text.secondary',
                    fontWeight: 400,
                  }}
                >
                  Buscar
                </Typography>
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    px: 0.75,
                    height: 22,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: alpha('#000', 0.12),
                    bgcolor: '#fff',
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    color: 'text.secondary',
                    letterSpacing: 0.2,
                  }}
                >
                  Ctrl+K
                </Box>
              </ButtonBase>
            </Tooltip>
          </Box>
        )}

        <Box
          sx={{
            flex: { xs: '0 0 auto', md: '1 1 0' },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: { xs: 0.5, sm: 0.75, md: 1 },
            minWidth: 0,
          }}
        >
          <Typography
            variant="h6"
            noWrap
            sx={{
              fontWeight: 600,
              color: 'primary.main',
              letterSpacing: '0.02em',
              fontFamily: [
                '-apple-system',
                'BlinkMacSystemFont',
                '"Segoe UI"',
                'Roboto',
                '"Helvetica Neue"',
                'Arial',
                'sans-serif',
              ].join(','),
              display: { xs: 'none', lg: 'block' },
              fontSize: '1rem',
              flexShrink: 0,
            }}
          >
            {getSiteName()}
          </Typography>
          {tenantName && !isMobile && (
            <Box sx={{ display: { xs: 'none', md: 'inline-flex' }, minWidth: 0 }}>
              <TenantSwitcher
                variant="full"
                tenants={tenants}
                tenantId={tenantId}
                tenantName={tenantName}
                tenantCode={tenantCode}
                onSwitch={async (id) => {
                  await switchTenant(id);
                }}
              />
            </Box>
          )}
          <FechaOperativaChip />
          {isMobile && (
            <Tooltip title="Buscar pantallas y funcionalidades (Ctrl+K)">
              <IconButton
                size="small"
                onClick={() => setBuscadorOpen(true)}
                sx={{
                  flexShrink: 0,
                  color: '#00548f',
                  '&:hover': { backgroundColor: 'rgba(0, 84, 143, 0.08)' },
                }}
              >
                <SearchIcon />
              </IconButton>
            </Tooltip>
          )}
          <IconButton
            onClick={handleMenu}
            size={isXs ? 'small' : 'medium'}
            sx={{
              flexShrink: 0,
              padding: { xs: 0.25, sm: 0.5 },
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
              },
            }}
          >
            <Avatar
              sx={{
                bgcolor: '#1976d2',
                color: 'white',
                width: { xs: 28, sm: 32, md: 35 },
                height: { xs: 28, sm: 32, md: 35 },
                fontSize: { xs: '0.85rem', sm: '1rem' },
              }}
            >
              {user?.username?.charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
          <PageHelpButton
            open={pageHelpOpen}
            onOpenChange={onPageHelpOpenChange}
            size={isXs ? 'small' : 'medium'}
          />
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <MenuItem onClick={handleEditMyProfile} disabled={!user?.username}>
              <ListItemText
                primary={user?.displayName || user?.username || '—'}
                secondary="Mis datos"
                primaryTypographyProps={{ fontWeight: 600 }}
              />
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleClose();
                navigate('/configuraciones/claves-agente');
              }}
              disabled={!user?.username}
            >
              <ListItemText
                primary="Claves de agente"
                secondary="LLM / OpenClaw / MCP"
              />
            </MenuItem>
            <MenuItem onClick={handleDownloadApk} disabled={apkDownloading}>
              <Typography>
                {apkDownloading ? 'Descargando App Vendedores...' : 'Descargar App Vendedores'}
              </Typography>
            </MenuItem>
            {puedeAdministrar && (
              <MenuItem
                onClick={() => {
                  handleClose();
                  navigate('/configuraciones');
                }}
              >
                <ListItemText
                  primary="Configuración de la empresa"
                  secondary="Usuarios, maestros y correo"
                />
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                handleClose();
                requestOpenEditor();
              }}
            >
              <Typography>Fecha operativa…</Typography>
            </MenuItem>
            <MenuItem onClick={handleChangePassword}>
              <Typography>Cambiar Contraseña</Typography>
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <Typography>Cerrar Sesión</Typography>
            </MenuItem>
            {puedeCambiarTenant && (
              <MenuItem onClick={handleLogoutAll}>
                <ListItemText
                  primary="Cerrar sesión en todas las pestañas"
                  secondary="Sale de todas las empresas abiertas"
                />
              </MenuItem>
            )}
          </Menu>
        </Box>
      </Toolbar>
      <ChangePasswordDialog open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} />
      <BuscadorAccesosModal open={buscadorOpen} onClose={() => setBuscadorOpen(false)} />
    </AppBar>
  );
};

export default Header;
